import React from 'react';
import ReactHtmlParser from 'react-html-parser';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { gql, useMutation, useQuery } from '@apollo/client';
import { faExclamationTriangle } from '@fortawesome/pro-regular-svg-icons';
import { Admission, Hospital } from '@quality24/inpatient-typings';
import { Alert, Spinner, useToast } from '@quality24/design-system';

import { useAuth } from '@/inpatient-patient-pwa/contexts/auth/useAuth';
import ConfirmationModal from '@/inpatient-patient-pwa/organisms/ConfirmationModal';
import PrivateRouter from '@/inpatient-patient-pwa/routes/PrivateRouter';
import {
  getLgpdStatus,
  storeLgpdStatus,
} from '@/inpatient-patient-pwa/services/storage';

/**
 * GraphQL Mutation
 */

export const LGPD_MUTATION = gql`
  mutation lgpdMutation(
    $admissionId: UUID!
    $state: EnumDataProtectionAgreementState!
  ) {
    updateAdmissionsViewById(
      input: {
        id: $admissionId
        patch: { dataProtectionAgreementState: $state }
      }
    ) {
      clientMutationId
    }
  }
`;

/**
 * GraphQL Query
 */

export const DATA_AGREEMENT_QUERY = gql`
  query DataAgreementQuery($hospitalId: UUID!, $admissionId: UUID!) {
    hospital: hospitalById(id: $hospitalId) {
      id
      lgpdConsentHtml
    }
    admission: admissionById(id: $admissionId) {
      id
      dataProtectionAgreementState
    }
  }
`;

export interface QueryResult {
  hospital: Required<Pick<Hospital, 'id' | 'lgpdConsentHtml'>>;
  admission: Required<Pick<Admission, 'id' | 'dataProtectionAgreementState'>>;
}

const LgpdConsentPage: React.FC = () => {
  const [promptConfirmation, setPromptConfirmation] = React.useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation();
  const {
    user: { admissionId },
    user,
    logout,
  } = useAuth();
  const [lgpdMutation, { loading: mutationLoading }] =
    useMutation(LGPD_MUTATION);

  const { data, loading } = useQuery<QueryResult>(DATA_AGREEMENT_QUERY, {
    variables: { hospitalId: user.hospitalId, admissionId },
    skip: getLgpdStatus() === 'NA',
  });

  const shouldCheckLgpd = React.useMemo(() => {
    if (!data) return false;
    if (!data.hospital.lgpdConsentHtml) {
      storeLgpdStatus('NA');
      return false;
    }
    if (data.admission.dataProtectionAgreementState === 'ACCEPTED') {
      storeLgpdStatus('ACCEPTED');
      return false;
    }

    return true;
  }, [data]);

  // The consent term is shown in pt-BR whatever the app language: the column
  // has no translation. To translate it, add `lgpd_consent_html_i18n` to
  // `hospital` following the `@i18n` column pattern and query
  // `lgpdConsentHtmlI18N` instead. Not done yet since no client uses this page.
  const html = ReactHtmlParser(data?.hospital.lgpdConsentHtml as string);

  const acceptLgpdAgreement = React.useCallback(async () => {
    try {
      await lgpdMutation({
        variables: {
          admissionId,
          state: 'ACCEPTED',
        },
      });
      storeLgpdStatus('ACCEPTED');
      navigate('/');
      toast(t('lgpd.consentSuccess'));
    } catch (err) {
      toast(t('errors.genericRequest'));
    }
  }, [admissionId, lgpdMutation, toast, navigate, t]);

  const refuseLgpdAgreement = React.useCallback(async () => {
    try {
      await lgpdMutation({
        variables: {
          admissionId,
          state: 'REFUSED',
        },
      });

      logout();
    } catch (err) {
      toast(t('errors.genericRequest'));
    }
  }, [admissionId, lgpdMutation, logout, toast, t]);

  if (!shouldCheckLgpd) return <PrivateRouter />;

  return (
    <ConfirmationModal
      onConfirm={acceptLgpdAgreement}
      onCancel={
        promptConfirmation
          ? refuseLgpdAgreement
          : () => setPromptConfirmation(true)
      }
      onHide={() => {
        navigate('/');
      }}
      confirmLabel={t('lgpd.agree')}
      cancelLabel={
        promptConfirmation ? t('lgpd.refuseAndLogout') : t('lgpd.refuse')
      }
      confirmBtnClassName="fw-light"
      show
    >
      {loading || mutationLoading ? (
        <div className="d-flex flex-justify-center p-4">
          <Spinner />
        </div>
      ) : (
        <>
          {html}
          {promptConfirmation && (
            <Alert
              variant="warning"
              className="my-2"
              icon={faExclamationTriangle}
            >
              {t('lgpd.confirmRefuse')}
            </Alert>
          )}
        </>
      )}
    </ConfirmationModal>
  );
};

export default LgpdConsentPage;
